import React, { useState, useEffect } from 'react';
import { masterSupabase } from '../lib/supabase-master.js';
import { 
  Building, 
  Plus, 
  Search, 
  Filter, 
  Star, 
  Users, 
  TrendingUp, 
  Eye, 
  Send, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Home,
  Euro,
  Calendar,
  MapPin,
  Phone,
  Mail,
  FileText,
  Download,
  Upload,
  Target,
  Zap,
  Globe,
  Share2,
  BarChart3,
  Award,
  Sparkles,
  Map,
  Building2,
  Store,
  Briefcase,
  Warehouse,
  Presentation,
  Rocket,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Shield,
  Activity,
  FileCheck,
  AlertTriangle,
  Menu,
  Loader,
  Calculator,
  Brain,
  Wand2,
  Settings,
  Palette,
  Image,
  Camera,
  Save
} from 'lucide-react';

// Import PDF and Document components
import ExposePricingEngine from '../components/ExposePricingEngine.js';
import PDFExportEngine from '../components/PDFExportEngine.js';
import DocumentSelectorWidget from '../components/DocumentSelectorWidget.jsx';

// UUID Validation Helper
const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuid && typeof uuid === 'string' && uuidRegex.test(uuid);
};

// Safe Supabase Query Helper
const safeSupabaseQuery = async (queryFn, fallbackValue = []) => {
  try {
    const result = await queryFn();
    if (result.error) {
      console.error('Supabase query error:', result.error);
      return { data: fallbackValue, error: result.error };
    }
    return result;
  } catch (error) {
    console.error('Supabase query exception:', error);
    return { data: fallbackValue, error };
  }
};

// Enhanced Pricing Calculator with KI Features
class EnhancedPricingCalculator {
  static calculateAdvancedPricing(unit, property) {
    const baseRentPerSqm = 12;
    const unitSize = unit?.size_sqm || 50;
    const baseRent = baseRentPerSqm * unitSize;
    
    const locationFactors = {
      'München': 1.4,
      'Frankfurt': 1.3,
      'Hamburg': 1.2,
      'Berlin': 1.1,
      'default': 1.0
    };
    
    const locationFactor = locationFactors[property?.city] || locationFactors.default;
    const adjustedRent = Math.round(baseRent * locationFactor);
    const utilities = Math.round(unitSize * 3.5);
    
    return {
      baseRent: Math.round(baseRent),
      adjustedRent: adjustedRent,
      utilities: utilities,
      deposit: adjustedRent * 2.5,
      totalCost: adjustedRent + utilities,
      pricePerSqm: Math.round(adjustedRent / unitSize),
      marketComparison: {
        belowMarket: adjustedRent * 0.9,
        aboveMarket: adjustedRent * 1.1,
        premium: adjustedRent * 1.2
      }
    };
  }

  static generateEnhancedDescription(unit, property, marketingType = 'rent') {
    const rooms = unit?.rooms || 2;
    const roomText = rooms === 1 ? '1-Zimmer-Apartment' : `${rooms}-Zimmer-Wohnung`;
    const size = unit?.size_sqm || 50;
    const city = property?.city || 'der Stadt';
    
    const descriptions = {
      rent: {
        title: `${roomText} in ${city} zu vermieten`,
        description: `Schöne ${roomText} mit ${size}m² Wohnfläche in ${city}. Die Wohnung bietet eine gute Raumaufteilung und ist ideal für ${rooms <= 2 ? 'Singles oder Paare' : 'Familien'}. Moderne Ausstattung und zentrale Lage machen diese Immobilie zu einem attraktiven Mietobjekt.`,
        highlights: [
          `${rooms} Zimmer`,
          `${size}m² Wohnfläche`,
          `Zentrale Lage in ${city}`,
          'Moderne Ausstattung',
          'Gute Verkehrsanbindung'
        ]
      },
      sale: {
        title: `${roomText} in ${city} zu verkaufen`,
        description: `Attraktive ${roomText} mit ${size}m² Wohnfläche in ${city}. Diese Immobilie bietet eine ausgezeichnete Kapitalanlage oder ein schönes Zuhause. Die zentrale Lage und die gute Ausstattung machen diese Wohnung zu einer wertvollen Investition.`,
        highlights: [
          `${rooms} Zimmer`,
          `${size}m² Wohnfläche`,
          `Wertstabile Lage in ${city}`,
          'Kapitalanlage-Potenzial',
          'Sofort verfügbar'
        ]
      }
    };
    
    return descriptions[marketingType] || descriptions.rent;
  }
}

// Property Type Configuration
const getPropertyTypeConfig = (propertyType) => {
  const configs = {
    'Mehrfamilienhaus': { 
      icon: Building2, 
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      emoji: '🏢'
    },
    'Gewerbeimmobilie': { 
      icon: Store, 
      color: 'bg-green-100 text-green-800 border-green-200',
      emoji: '🏪'
    },
    'Einfamilienhaus': { 
      icon: Home, 
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      emoji: '🏠'
    },
    'Büro': { 
      icon: Briefcase, 
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      emoji: '💼'
    },
    'Lager': { 
      icon: Warehouse, 
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      emoji: '🏭'
    }
  };
  
  return configs[propertyType] || configs['Mehrfamilienhaus'];
};

// Property Type Badge Component
const PropertyTypeBadge = ({ propertyType }) => {
  const config = getPropertyTypeConfig(propertyType);
  
  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.color}`}>
      <span className="mr-2">{config.emoji}</span>
      {propertyType || 'Unbekannt'}
    </div>
  );
};

// Platform Badge Component
const PlatformBadge = ({ platform, active = false }) => {
  const platforms = {
    immowelt: { name: 'Immowelt', color: 'bg-blue-100 text-blue-800', icon: '🏠' },
    regionalimmobilien24: { name: 'Regionalimmobilien24', color: 'bg-green-100 text-green-800', icon: '🏢' },
    estatesync: { name: 'EstateSync', color: 'bg-purple-100 text-purple-800', icon: '🔄' },
    kleinanzeigen: { name: 'Kleinanzeigen', color: 'bg-yellow-100 text-yellow-800', icon: '📋' }
  };

  const config = platforms[platform] || { name: platform, color: 'bg-gray-100 text-gray-800', icon: '📄' };

  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${config.color} ${active ? 'ring-2 ring-blue-500' : ''}`}>
      <span className="mr-1">{config.icon}</span>
      {config.name}
      {active && <CheckCircle className="w-3 h-3 ml-1" />}
    </div>
  );
};

// EXTENDED ROADMAP SLIDES DATA
const roadmapSlides = [
  {
    id: 1,
    title: "Clara Marketplace Roadmap",
    subtitle: "Von MVP zur kompletten Vermarktungsplattform",
    content: (
      <div className="text-center space-y-8">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl">
          <h2 className="text-4xl font-bold mb-4">Clara Marketplace</h2>
          <p className="text-xl opacity-90">Die Zukunft der Immobilien-Vermarktung</p>
        </div>
        
        <div className="grid grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">€15</div>
            <div className="text-lg text-blue-600 dark:text-blue-400">Mrd.</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Marktpotenzial</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">385%</div>
            <div className="text-lg text-green-600 dark:text-green-400">ROI in 3</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Jahren</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">€2M+</div>
            <div className="text-lg text-purple-600 dark:text-purple-400">ARR Ziel</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">2027</div>
          </div>
        </div>
        
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mainzerstraße 120, 69199 Saarbrücken
        </div>
      </div>
    )
  },
  {
    id: 2,
    title: "Contract Readiness Gate",
    subtitle: "Verträge & Compliance vor Phase 1",
    content: (
      <div className="space-y-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <FileCheck className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Kritische Verträge</h3>
          </div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              <span>ImmoScout24 API-Vertrag & SLA</span>
            </li>
            <li className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              <span>Immowelt Partner-Agreement</span>
            </li>
            <li className="flex items-center">
              <Clock className="w-4 h-4 text-yellow-600 mr-2" />
              <span>DSGVO-Compliance für Lead-Routing</span>
            </li>
          </ul>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">API-Limits</h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• 1000 Listings/Monat</li>
              <li>• 10.000 API-Calls/Tag</li>
              <li>• 99.5% Uptime SLA</li>
            </ul>
          </div>
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Compliance</h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• DSGVO-konforme Datenverarbeitung</li>
              <li>• Lead-Einverständnis</li>
              <li>• Datenschutz-Audit</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 3,
    title: "Marktchance",
    subtitle: "€15 Milliarden Potenzial im DACH-Raum",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">Marktgröße</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">Deutschland</span>
                <span className="font-bold text-blue-900 dark:text-blue-100">€12 Mrd.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">Österreich</span>
                <span className="font-bold text-blue-900 dark:text-blue-100">€2 Mrd.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">Schweiz</span>
                <span className="font-bold text-blue-900 dark:text-blue-100">€1 Mrd.</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">Wachstum</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-green-700 dark:text-green-300">CAGR 2024-2027</span>
                <span className="font-bold text-green-900 dark:text-green-100">+15%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-green-700 dark:text-green-300">Digitalisierung</span>
                <span className="font-bold text-green-900 dark:text-green-100">+25%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-green-700 dark:text-green-300">PropTech</span>
                <span className="font-bold text-green-900 dark:text-green-100">+40%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-slate-900 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Markttrends</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Automatisierung der Vermarktung</li>
              <li>• Multi-Portal-Management</li>
              <li>• KI-gestützte Preisfindung</li>
            </ul>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Integrierte Verwaltungsplattformen</li>
              <li>• Datengetriebene Entscheidungen</li>
              <li>• Cross-Selling-Potenziale</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 4,
    title: "Competitive Analysis",
    subtitle: "Clara360 vs. ohne-makler.net",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h3 className="font-semibold text-red-900 dark:text-red-100 mb-3 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              ohne-makler.net
            </h3>
            <ul className="space-y-2 text-sm text-red-700 dark:text-red-300">
              <li>• 15 Jahre alte Technologie</li>
              <li>• Keine Verwaltungsintegration</li>
              <li>• Statische Preismodelle</li>
              <li>• Begrenzte Automatisierung</li>
              <li>• €2.999 Full-Service</li>
            </ul>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Clara360 Advantage
            </h3>
            <ul className="space-y-2 text-sm text-green-700 dark:text-green-300">
              <li>• Moderne React/Supabase Tech</li>
              <li>• Nahtlose Verwaltungsintegration</li>
              <li>• KI-gestützte Preisfindung</li>
              <li>• Vollautomatisierte Workflows</li>
              <li>• €2.499 Full-Service (-17%)</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">Unique Selling Propositions</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-blue-800 dark:text-blue-200">Datenintegration</div>
              <div className="text-blue-600 dark:text-blue-400">Objekt bereits im System → 1-Klick-Exposé</div>
            </div>
            <div>
              <div className="font-medium text-blue-800 dark:text-blue-200">Unit-Level Marketing</div>
              <div className="text-blue-600 dark:text-blue-400">Einzelne Wohnungen aus MFH vermarkten</div>
            </div>
            <div>
              <div className="font-medium text-blue-800 dark:text-blue-200">Cross-Selling</div>
              <div className="text-blue-600 dark:text-blue-400">Energieausweis + Versicherung + Finanzierung</div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 5,
    title: "KPI-Matrix",
    subtitle: "Messbare Erfolge je Phase",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Phase 1 (Q1 2025)</h3>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• Time-to-Publish: ≤ 15 Min</li>
              <li>• Cost-per-Listing: €59-€149</li>
              <li>• Lead-per-Portal: 5-15/Monat</li>
              <li>• Portal-Sync: ≤ 5 Min</li>
              <li>• Error-Rate: ≤ 2%</li>
            </ul>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 dark:text-green-100 mb-3">Phase 2 (Q2-Q3 2025)</h3>
            <ul className="space-y-2 text-sm text-green-700 dark:text-green-300">
              <li>• KI-Pricing-Uplift: +8-15%</li>
              <li>• Conversion je Portal: 3-8%</li>
              <li>• Cross-Selling-Rate: 25%</li>
              <li>• Customer-Satisfaction: ≥ 4.5/5</li>
              <li>• Churn-Rate: ≤ 5%</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-3">Phase 3 (2026)</h3>
            <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-300">
              <li>• API-Adoption: 50+ Partner</li>
              <li>• Net Revenue Retention: 120%</li>
              <li>• White-Label-Revenue: €500K</li>
              <li>• Market-Share: 5% DACH</li>
              <li>• Team-Size: 25 FTE</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-slate-900 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Tech-Meilensteine</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• MVP Happy Path: 1 Objekt → 3 Portale ≤ 15 Min</li>
              <li>• SLA: Sync-Latenz ≤ 5 Min, Error-Retry ≤ 2 Min</li>
              <li>• Payment: Checkout-Erfolg ≥ 95%</li>
            </ul>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• KI-Accuracy: Preisvorhersage ±5%</li>
              <li>• API-Performance: 99.9% Uptime</li>
              <li>• Mobile-First: 80% Mobile-Traffic</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 6,
    title: "Killer-Features",
    subtitle: "KI + Analytics + Cross-Selling",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4 flex items-center">
              <Brain className="w-5 h-5 mr-2" />
              KI-Features
            </h3>
            <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-300">
              <li>• Automatische Preisoptimierung</li>
              <li>• Zielgruppen-spezifische Texte</li>
              <li>• Optimale Portal-Auswahl</li>
              <li>• Conversion-Vorhersage</li>
              <li>• Markttrend-Analyse</li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics
            </h3>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• Portal-Performance-Dashboard</li>
              <li>• Lead-Attribution & ROI</li>
              <li>• Conversion-Tracking</li>
              <li>• A/B-Testing für Preise</li>
              <li>• Marktvergleich-Reports</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-900 dark:text-green-100 mb-3 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Cross-Selling Intelligence
          </h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-green-800 dark:text-green-200">Energieausweis</div>
              <div className="text-green-600 dark:text-green-400">Auto-Erkennung: "Bj. 1975 → Pflicht"</div>
            </div>
            <div>
              <div className="font-medium text-green-800 dark:text-green-200">Versicherung</div>
              <div className="text-green-600 dark:text-green-400">Gebäude- & Mietausfallversicherung</div>
            </div>
            <div>
              <div className="font-medium text-green-800 dark:text-green-200">Finanzierung</div>
              <div className="text-green-600 dark:text-green-400">Käufer-Finanzierung & Modernisierung</div>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Quick-Wins</h4>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            "Ein-Klick aus Verwaltung" Button • Exposé-Templates + KI-Autofill • Lead-Router mit Portal-Attribution • A/B-Testing für Preise (€59/€149 vs €49/€139)
          </div>
        </div>
      </div>
    )
  },
  {
    id: 7,
    title: "Revenue-Modell",
    subtitle: "3 Einnahmequellen für nachhaltiges Wachstum",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Listing-Gebühren</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Basic</span>
                <span className="font-bold text-blue-900 dark:text-blue-100">€59</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Premium</span>
                <span className="font-bold text-blue-900 dark:text-blue-100">€149</span>
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                Pro Anzeige, alle Portale
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h3 className="font-semibold text-green-900 dark:text-green-100 mb-3">Subscription</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700 dark:text-green-300">Starter</span>
                <span className="font-bold text-green-900 dark:text-green-100">€29</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700 dark:text-green-300">Professional</span>
                <span className="font-bold text-green-900 dark:text-green-100">€79</span>
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                Pro Monat, unbegrenzte Listings
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-3">Full-Service</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-purple-700 dark:text-purple-300">Komplett</span>
                <span className="font-bold text-purple-900 dark:text-purple-100">€2.499</span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700 dark:text-purple-300">vs ohne-makler</span>
                <span className="font-bold text-red-600">€2.999</span>
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                Inkl. Fotos, Energieausweis, Vermarktung
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-slate-900 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Revenue-Projektion</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">€500K</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">ARR 2025</div>
              <div className="text-xs text-gray-500">100 Kunden</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">€1.2M</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">ARR 2026</div>
              <div className="text-xs text-gray-500">300 Kunden</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">€2M+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">ARR 2027</div>
              <div className="text-xs text-gray-500">500+ Kunden</div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 8,
    title: "Smart Exposé Evolution",
    subtitle: "Interaktive PDF-Revolution",
    content: (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-2">Smart Exposé Features</h3>
          <p className="opacity-90">Interaktive PDFs mit Analytics und Lead-Tracking</p>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Interaktive Features</h4>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• QR-Code → Clara360 Landingpage</li>
              <li>• 3D-Touren + Drohnenvideos</li>
              <li>• Direktes Anfrageformular im PDF</li>
              <li>• Eingebaute Terminbuchung</li>
              <li>• Social Media Integration</li>
            </ul>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-3">Analytics Dashboard</h4>
            <ul className="space-y-2 text-sm text-green-700 dark:text-green-300">
              <li>• "Exposé 34x geöffnet, 12x gescannt"</li>
              <li>• Lead-Tracking pro Portal</li>
              <li>• Conversion-Funnel-Analyse</li>
              <li>• Heatmaps für PDF-Bereiche</li>
              <li>• ROI-Berechnung je Kanal</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-3">Lead-Funnel Automation</h4>
          <div className="text-sm text-purple-700 dark:text-purple-300">
            <strong>Workflow:</strong> Anfrageformular im PDF → Clara360 CRM → Clara KI Chat → Automatische Lead-Qualifizierung → Weiterleitung an Verwalter
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Cross-Selling Intelligence</h4>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            Smart Upsell-Buttons im Exposé: "Haus Bj. 1975 → Energieausweis Pflicht" → Button: "Jetzt direkt für €79 bei Clara bestellen"
          </div>
        </div>
      </div>
    )
  },
  {
    id: 9,
    title: "Premium-Monetarisierung",
    subtitle: "Tiered Pricing Strategy",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Basic</h3>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Kostenlos</div>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li>• Standard PDF-Exposé</li>
              <li>• 3 Portal-Uploads</li>
              <li>• Basis-Analytics</li>
              <li>• E-Mail-Support</li>
            </ul>
            <div className="mt-4 text-xs text-gray-500">
              Kundenbindung & Akquise
            </div>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Premium</h3>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-2">€29-49</div>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• KI-optimierte Exposés</li>
              <li>• Alle Portal-Integrationen</li>
              <li>• Erweiterte Analytics</li>
              <li>• Lead-Tracking</li>
              <li>• Priority-Support</li>
            </ul>
            <div className="mt-4 text-xs text-blue-600 dark:text-blue-400">
              Hauptumsatzträger
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-3">Full-Service</h3>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100 mb-2">€2.499</div>
            <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-300">
              <li>• Professionelle Fotografie</li>
              <li>• Drohnenaufnahmen</li>
              <li>• 360°-Touren</li>
              <li>• Energieausweis-Service</li>
              <li>• Dedicated Account Manager</li>
            </ul>
            <div className="mt-4 text-xs text-purple-600 dark:text-purple-400">
              Partnernetzwerk-Revenue
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-900 dark:text-green-100 mb-3">Revenue-Streams</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-green-800 dark:text-green-200">Subscription (60%)</div>
              <div className="text-green-600 dark:text-green-400">€29-79/Monat wiederkehrend</div>
            </div>
            <div>
              <div className="font-medium text-green-800 dark:text-green-200">Transactional (25%)</div>
              <div className="text-green-600 dark:text-green-400">€59-149 pro Listing</div>
            </div>
            <div>
              <div className="font-medium text-green-800 dark:text-green-200">Partner-Commission (15%)</div>
              <div className="text-green-600 dark:text-green-400">10-20% auf Services</div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 10,
    title: "End-to-End Betriebssystem",
    subtitle: "Komplettes Immobilien-Ökosystem",
    content: (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-2">Clara360 Ökosystem</h3>
          <p className="opacity-90">Von der Verwaltung über die Vermarktung bis zum Vertragsabschluss</p>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Partnernetzwerk</h4>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• Professionelle Fotografie</li>
              <li>• Drohnen-Services</li>
              <li>• 360°-Tour-Anbieter</li>
              <li>• Energieberater-Netzwerk</li>
              <li>• Notar-Kooperationen</li>
              <li>• Finanzierungs-Partner</li>
            </ul>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-3">Clara KI Integration</h4>
            <ul className="space-y-2 text-sm text-green-700 dark:text-green-300">
              <li>• 24/7 Interessenten-Chat</li>
              <li>• Automatische Lead-Qualifizierung</li>
              <li>• Terminbuchung-Assistent</li>
              <li>• Preisoptimierung-Engine</li>
              <li>• Marktanalyse-Reports</li>
              <li>• Predictive Analytics</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-3">White-Label B2B-API</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-purple-800 dark:text-purple-200">Starter</div>
              <div className="text-purple-600 dark:text-purple-400">€199/Monat • 100 Listings</div>
            </div>
            <div>
              <div className="font-medium text-purple-800 dark:text-purple-200">Professional</div>
              <div className="text-purple-600 dark:text-purple-400">€499/Monat • 500 Listings</div>
            </div>
            <div>
              <div className="font-medium text-purple-800 dark:text-purple-200">Enterprise</div>
              <div className="text-purple-600 dark:text-purple-400">€999/Monat • Unlimited</div>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Competitive Positioning</h4>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            <strong>Konkurrenten:</strong> "Nur Inserat-Verteiler" → <strong>Clara360:</strong> "End-to-End Immobilien-Betriebssystem"
          </div>
        </div>
      </div>
    )
  },
  {
    id: 11,
    title: "Risk-Register",
    subtitle: "Proaktives Risikomanagement",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 dark:text-red-100 mb-3 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Technische Risiken
            </h4>
            <ul className="space-y-2 text-sm text-red-700 dark:text-red-300">
              <li>• Portal-Policy-Änderungen</li>
              <li>• API-Rate-Limits</li>
              <li>• Datenqualität-Issues</li>
              <li>• Skalierungs-Bottlenecks</li>
            </ul>
            <div className="mt-3 text-xs text-red-600 dark:text-red-400">
              <strong>Mitigation:</strong> Abstraktions-Layer, Fallback-APIs
            </div>
          </div>
          
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-3 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Business Risiken
            </h4>
            <ul className="space-y-2 text-sm text-orange-700 dark:text-orange-300">
              <li>• Fraud/Scam-Listings</li>
              <li>• DSGVO-Compliance</li>
              <li>• Competitor-Response</li>
              <li>• Market-Saturation</li>
            </ul>
            <div className="mt-3 text-xs text-orange-600 dark:text-orange-400">
              <strong>Mitigation:</strong> Verifizierung, DPA, First-Mover
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">Risk-Mitigation-Matrix</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-blue-800 dark:text-blue-200">Portal-Dependencies</div>
              <div className="text-blue-600 dark:text-blue-400">Multi-Portal-Strategy + eigene Reichweite</div>
            </div>
            <div>
              <div className="font-medium text-blue-800 dark:text-blue-200">Regulatory Changes</div>
              <div className="text-blue-600 dark:text-blue-400">Legal-Advisory + Compliance-Monitoring</div>
            </div>
            <div>
              <div className="font-medium text-blue-800 dark:text-blue-200">Tech-Disruption</div>
              <div className="text-blue-600 dark:text-blue-400">Agile Development + Innovation-Budget</div>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Success Factors</h4>
          <div className="text-sm text-green-700 dark:text-green-300">
            <strong>Critical Success Factors:</strong> Portal-Partnerships, Customer-Retention, Tech-Reliability, Team-Execution, Market-Timing
          </div>
        </div>
      </div>
    )
  },
  {
    id: 12,
    title: "Investment-Ask",
    subtitle: "€420.000 für 385% ROI",
    content: (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-xl text-center">
          <h2 className="text-3xl font-bold mb-2">€420.000</h2>
          <p className="text-xl opacity-90">Investment für Clara Marketplace</p>
          <div className="mt-4 text-lg">
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">385% ROI in 3 Jahren</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Investment-Breakdown</h4>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• Development Team: €180K (43%)</li>
              <li>• Portal-Integrationen: €80K (19%)</li>
              <li>• Marketing & Sales: €60K (14%)</li>
              <li>• Infrastructure: €40K (10%)</li>
              <li>• Legal & Compliance: €30K (7%)</li>
              <li>• Contingency: €30K (7%)</li>
            </ul>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-3">ROI-Projektion</h4>
            <ul className="space-y-2 text-sm text-green-700 dark:text-green-300">
              <li>• 2025: €500K ARR (119% ROI)</li>
              <li>• 2026: €1.2M ARR (186% ROI)</li>
              <li>• 2027: €2M+ ARR (385% ROI)</li>
              <li>• Break-Even: Monat 8</li>
              <li>• Payback-Period: 18 Monate</li>
              <li>• Exit-Valuation: €15-20M</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-3">Milestones & Funding-Tranches</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-purple-800 dark:text-purple-200">Tranche 1: €150K</div>
              <div className="text-purple-600 dark:text-purple-400">MVP + Portal-APIs (Q1 2025)</div>
            </div>
            <div>
              <div className="font-medium text-purple-800 dark:text-purple-200">Tranche 2: €170K</div>
              <div className="text-purple-600 dark:text-purple-400">KI-Features + Scale (Q2-Q3 2025)</div>
            </div>
            <div>
              <div className="font-medium text-purple-800 dark:text-purple-200">Tranche 3: €100K</div>
              <div className="text-purple-600 dark:text-purple-400">B2B-API + International (2026)</div>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-center">
          <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Next Steps</h4>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            <strong>Due Diligence:</strong> Tech-Demo • Financial-Model • Team-Interviews • Market-Validation • Legal-Review
          </div>
        </div>
      </div>
    )
  }
];

const MarketplacePage = () => {
  // State Management
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [units, setUnits] = useState({});
  const [loadingUnits, setLoadingUnits] = useState({});
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [marketingType, setMarketingType] = useState('rent'); // 'rent' or 'sale'

  // Roadmap State
  const [showRoadmapModal, setShowRoadmapModal] = useState(false);
  const [showFullSlides, setShowFullSlides] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // PDF and Document State
  const [exposeData, setExposeData] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('modern_apartment');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Initialize engines
  const [pricingEngine] = useState(() => new ExposePricingEngine());
  const [pdfEngine] = useState(() => new PDFExportEngine());

  // Load Properties
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const { data, error } = await safeSupabaseQuery(
        () => masterSupabase
          .from('properties')
          .select('*')
          .order('created_at', { ascending: false }),
        []
      );

      if (error) {
        console.error('Error loading properties:', error);
      } else {
        setProperties(data || []);
      }
    } catch (error) {
      console.error('Exception loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load Units for Property
  const loadUnits = async (propertyId) => {
    if (!isValidUUID(propertyId) || loadingUnits[propertyId]) return;

    try {
      setLoadingUnits(prev => ({ ...prev, [propertyId]: true }));
      
      const { data, error } = await safeSupabaseQuery(
        () => masterSupabase
          .from('units')
          .select('*')
          .eq('property_id', propertyId)
          .order('unit_identifier', { ascending: true }),
        []
      );

      if (error) {
        console.error('Error loading units:', error);
      } else {
        setUnits(prev => ({ ...prev, [propertyId]: data || [] }));
      }
    } catch (error) {
      console.error('Exception loading units:', error);
    } finally {
      setLoadingUnits(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  // Handle Create Listing
  const handleCreateListing = async (property, unit, type = 'rent') => {
    setSelectedProperty(property);
    setSelectedUnit(unit);
    setMarketingType(type);
    setShowCreateModal(true);
    
    // Generate expose data
    try {
      const pricing = EnhancedPricingCalculator.calculateAdvancedPricing(unit, property);
      const description = EnhancedPricingCalculator.generateEnhancedDescription(unit, property, type);
      
      setExposeData({
        pricing,
        description,
        marketingType: type
      });
    } catch (error) {
      console.error('Error generating expose data:', error);
    }
  };

  // Handle Documents Selected
  const handleDocumentsSelected = (documents) => {
    setSelectedDocuments(documents);
    setShowDocumentSelector(false);
  };

  // Generate PDF Preview
  const generatePDFPreview = async () => {
    try {
      setGeneratingPDF(true);
      
      const pdfData = {
        property: selectedProperty,
        unit: selectedUnit,
        pricing: exposeData.pricing,
        description: exposeData.description,
        documents: {
          photos: selectedDocuments.filter(doc => doc.category === 'photos'),
          floorplans: selectedDocuments.filter(doc => doc.category === 'floorplans'),
          certificates: selectedDocuments.filter(doc => doc.category === 'certificates'),
          legal: selectedDocuments.filter(doc => doc.category === 'legal')
        },
        template: selectedTemplate
      };

      const pdf = await pdfEngine.generateExpose(pdfData);
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      setPdfPreviewUrl(pdfUrl);
      setShowPDFPreview(true);
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      alert('PDF-Vorschau konnte nicht erstellt werden: ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Export PDF
  const exportPDF = async () => {
    try {
      setGeneratingPDF(true);
      
      const pdfData = {
        property: selectedProperty,
        unit: selectedUnit,
        pricing: exposeData.pricing,
        description: exposeData.description,
        documents: {
          photos: selectedDocuments.filter(doc => doc.category === 'photos'),
          floorplans: selectedDocuments.filter(doc => doc.category === 'floorplans'),
          certificates: selectedDocuments.filter(doc => doc.category === 'certificates'),
          legal: selectedDocuments.filter(doc => doc.category === 'legal')
        },
        template: selectedTemplate
      };

      const result = await pdfEngine.exportPDF(pdfData);
      
      if (result.success) {
        alert(`PDF erfolgreich erstellt: ${result.filename}`);
      } else {
        alert('PDF-Export fehlgeschlagen: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('PDF-Export fehlgeschlagen: ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Filter Properties
  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedPropertyType === 'all' || property.property_type === selectedPropertyType;
    return matchesSearch && matchesType;
  });

  // Property Card Component
  const PropertyCard = ({ property }) => {
    const propertyUnits = units[property.id] || [];
    const isLoadingUnits = loadingUnits[property.id];
    const config = getPropertyTypeConfig(property.property_type);
    const IconComponent = config.icon;

    return (
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
        {/* Property Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <div className={`${config.color.replace('text-', 'bg-').replace('border-', '').split(' ')[0]} p-2 rounded-lg mr-3`}>
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                <PropertyTypeBadge propertyType={property.property_type} />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {property.address}
              </h3>
              
              <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm">
                <MapPin className="w-4 h-4 mr-1" />
                {property.city}, {property.postal_code}
              </div>
            </div>
          </div>
        </div>

        {/* Units Section */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Verfügbare Einheiten
            </h4>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {isLoadingUnits ? (
                <div className="flex items-center">
                  <Loader className="w-4 h-4 animate-spin mr-1" />
                  Laden...
                </div>
              ) : (
                `${propertyUnits.length} Einheit${propertyUnits.length !== 1 ? 'en' : ''}`
              )}
            </span>
          </div>

          {propertyUnits.length > 0 ? (
            <div className="space-y-3">
              {propertyUnits.slice(0, 3).map((unit) => (
                <div key={unit.id} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {unit.unit_identifier || `Einheit ${unit.id?.slice(0, 8)}`}
                        </span>
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {unit.rooms} Zimmer
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-600 dark:text-gray-400 space-x-4">
                        <span>{unit.size_sqm}m²</span>
                        {unit.monthly_rent && <span>€{unit.monthly_rent}/Monat</span>}
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCreateListing(property, unit, 'rent')}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        Vermieten
                      </button>
                      <button
                        onClick={() => handleCreateListing(property, unit, 'sale')}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        Verkaufen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {propertyUnits.length > 3 && (
                <div className="text-center">
                  <button
                    onClick={() => loadUnits(property.id)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm font-medium"
                  >
                    +{propertyUnits.length - 3} weitere Einheiten anzeigen
                  </button>
                </div>
              )}
            </div>
          ) : !isLoadingUnits ? (
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Keine Einheiten verfügbar
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // Enhanced Create Listing Modal
  const CreateListingModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="bg-blue-600 p-2 rounded-lg mr-3">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Exposé erstellen
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedProperty?.address} - {selectedUnit?.unit_identifier} ({marketingType === 'rent' ? 'Vermietung' : 'Verkauf'})
              </p>
            </div>
          </div>
          
          <button
            onClick={() => {
              setShowCreateModal(false);
              setSelectedProperty(null);
              setSelectedUnit(null);
              setSelectedDocuments([]);
              setPdfPreviewUrl(null);
              setShowPDFPreview(false);
              setShowDocumentSelector(false);
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-[calc(95vh-80px)]">
          {/* Left Panel - Configuration */}
          <div className="w-1/2 p-6 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            {/* Exposé Data Preview */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Exposé-Daten
              </h4>
              
              {exposeData.pricing && Object.keys(exposeData.pricing).length > 0 ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {marketingType === 'rent' ? 'Empfohlene Miete:' : 'Empfohlener Preis:'}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      €{exposeData.pricing.adjustedRent || 'N/A'}
                    </span>
                  </div>
                  {marketingType === 'rent' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Nebenkosten:</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          €{exposeData.pricing.utilities || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Kaution:</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          €{exposeData.pricing.deposit || 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Preis/m²:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      €{exposeData.pricing.pricePerSqm || 'N/A'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 flex items-center">
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Generiere Exposé-Daten...
                  </span>
                </div>
              )}
            </div>

            {/* Enhanced Description Section */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <Wand2 className="w-4 h-4 mr-2" />
                Beschreibung
              </h4>
              
              {exposeData.description ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Titel
                    </label>
                    <input
                      type="text"
                      value={exposeData.description.title}
                      onChange={(e) => setExposeData(prev => ({
                        ...prev,
                        description: { ...prev.description, title: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Beschreibung
                    </label>
                    <textarea
                      value={exposeData.description.description}
                      onChange={(e) => setExposeData(prev => ({
                        ...prev,
                        description: { ...prev.description, description: e.target.value }
                      }))}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white resize-none"
                    />
                  </div>
                  
                  <button
                    onClick={() => {
                      const newDescription = EnhancedPricingCalculator.generateEnhancedDescription(
                        selectedUnit, 
                        selectedProperty, 
                        marketingType
                      );
                      setExposeData(prev => ({ ...prev, description: newDescription }));
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    Erweiterte Beschreibung generieren
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 flex items-center">
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Generiere Beschreibung...
                  </span>
                </div>
              )}
            </div>

            {/* Template Selection */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <Palette className="w-4 h-4 mr-2" />
                Template-Auswahl
              </h4>
              
              <div className="space-y-2">
                {pdfEngine.getAvailableTemplates(selectedProperty?.property_type || 'Mehrfamilienhaus').map((template) => (
                  <label key={template.key} className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="radio"
                      name="template"
                      value={template.key}
                      checked={selectedTemplate === template.key}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {template.layout} Layout
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Document Selection */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <Image className="w-4 h-4 mr-2" />
                Dokumente & Fotos
              </h4>
              
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Ausgewählt:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedDocuments.length} Dokument{selectedDocuments.length !== 1 ? 'e' : ''}
                  </span>
                </div>
                
                {selectedDocuments.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedDocuments.filter(doc => doc.category === 'photos').length} Fotos, {' '}
                    {selectedDocuments.filter(doc => doc.category === 'floorplans').length} Grundrisse, {' '}
                    {selectedDocuments.filter(doc => doc.category === 'certificates').length} Zertifikate
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setShowDocumentSelector(!showDocumentSelector)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center"
              >
                <Camera className="w-4 h-4 mr-2" />
                Dokumente auswählen
              </button>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={generatePDFPreview}
                disabled={generatingPDF || selectedDocuments.length === 0}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center"
              >
                {generatingPDF ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Generiere PDF...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    PDF-Vorschau
                  </>
                )}
              </button>
              
              <button
                onClick={exportPDF}
                disabled={generatingPDF || selectedDocuments.length === 0}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF herunterladen
              </button>
            </div>
          </div>

          {/* Right Panel - Document Selector or PDF Preview */}
          <div className="w-1/2 overflow-y-auto">
            {showDocumentSelector ? (
              <DocumentSelectorWidget
                propertyId={selectedProperty?.id}
                unitId={selectedUnit?.id}
                onDocumentsSelected={handleDocumentsSelected}
                selectedDocuments={selectedDocuments}
                allowUpload={true}
                showStats={true}
                compact={false}
              />
            ) : showPDFPreview && pdfPreviewUrl ? (
              <div className="h-full flex flex-col">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    PDF-Vorschau
                  </h4>
                </div>
                <div className="flex-1">
                  <iframe
                    src={pdfPreviewUrl}
                    className="w-full h-full border-0"
                    title="PDF Preview"
                  />
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Exposé-Vorschau
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Wählen Sie Dokumente aus und generieren Sie eine PDF-Vorschau
                  </p>
                  <button
                    onClick={() => setShowDocumentSelector(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    Dokumente auswählen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Title */}
            <div className="flex items-center">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                Marketplace
              </h1>
            </div>

            {/* Desktop Roadmap Button & Mobile Menu Button */}
            <div className="flex items-center space-x-4">
              {/* Desktop Roadmap Button */}
              <button
                onClick={() => setShowRoadmapModal(true)}
                className="hidden md:flex bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium items-center transition-all"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Roadmap
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
              <button
                onClick={() => {
                  setShowRoadmapModal(true);
                  setShowMobileMenu(false);
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Roadmap anzeigen
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subtitle */}
        <p className="text-gray-600 dark:text-gray-400 mb-8 text-center">
          Verwalten Sie Ihre Immobilienanzeigen auf allen wichtigen Plattformen
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Aktive Anzeigen</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {properties.length}
                </p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Gesamte Aufrufe</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">1,234</p>
              </div>
              <div className="bg-green-100 dark:bg-green-900 p-3 rounded-lg">
                <Eye className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Anfragen</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">56</p>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Erfolgsrate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">89%</p>
              </div>
              <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Properties durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
            />
          </div>
          
          <select
            value={selectedPropertyType}
            onChange={(e) => setSelectedPropertyType(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          >
            <option value="all">Alle Immobilientypen</option>
            <option value="Mehrfamilienhaus">🏢 Mehrfamilienhaus</option>
            <option value="Gewerbeimmobilie">🏪 Gewerbeimmobilie</option>
            <option value="Einfamilienhaus">🏠 Einfamilienhaus</option>
            <option value="Büro">💼 Büro</option>
            <option value="Lager">🏭 Lager</option>
          </select>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Lade Properties...</p>
            </div>
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Keine Properties gefunden
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || selectedPropertyType !== 'all' 
                ? 'Versuchen Sie andere Suchkriterien.' 
                : 'Fügen Sie Properties hinzu, um mit der Vermarktung zu beginnen.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredProperties.map((property) => {
              // Load units when property card is rendered
              if (!units[property.id] && !loadingUnits[property.id]) {
                loadUnits(property.id);
              }
              
              return <PropertyCard key={property.id} property={property} />;
            })}
          </div>
        )}
      </main>

      {/* Create Listing Modal */}
      {showCreateModal && <CreateListingModal />}

      {/* Roadmap Modal */}
      {showRoadmapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="bg-blue-600 p-2 rounded-lg mr-3">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Clara Marketplace Roadmap
                </h3>
              </div>
              <button
                onClick={() => setShowRoadmapModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-900 dark:text-white">MVP Fertig</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-900 dark:text-white">PDF-Generator</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-900 dark:text-white">Dokument-Integration</span>
                </div>
                <div className="flex items-center">
                  <Loader className="w-5 h-5 text-blue-500 mr-3 animate-spin" />
                  <span className="text-gray-900 dark:text-white">Portal-APIs</span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="text-sm font-medium text-blue-900 dark:text-blue-100">Phase 1 (Q1 2025)</div>
                  <div className="text-xs text-blue-700 dark:text-blue-300">Multi-Portal Integration</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="text-sm font-medium text-purple-900 dark:text-purple-100">Phase 2 (Q2-Q3 2025)</div>
                  <div className="text-xs text-purple-700 dark:text-purple-300">KI-Features & Analytics</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="text-sm font-medium text-green-900 dark:text-green-100">Phase 3 (2026)</div>
                  <div className="text-xs text-green-700 dark:text-green-300">B2B-API & Skalierung</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-lg font-bold text-blue-600">€500K</div>
                  <div className="text-xs text-gray-500">ARR 2025</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-purple-600">€1.2M</div>
                  <div className="text-xs text-gray-500">ARR 2026</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-600">€2M+</div>
                  <div className="text-xs text-gray-500">ARR 2027</div>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowRoadmapModal(false);
                  setShowFullSlides(true);
                }}
                className="w-full mt-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Vollständige Präsentation anzeigen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Slides Modal */}
      {showFullSlides && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="w-full max-w-6xl mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-xl overflow-hidden">
            {/* Slide Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="bg-blue-600 p-2 rounded-lg mr-3">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Clara Marketplace Roadmap
                </h3>
                <button 
                  onClick={() => alert('PDF-Download wird implementiert...')}
                  className="ml-4 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  PDF
                </button>
                <button className="ml-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm flex items-center">
                  <Maximize2 className="w-4 h-4 mr-1" />
                </button>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Slide {currentSlide + 1} von {roadmapSlides.length}
                </span>
                <button
                  onClick={() => setShowFullSlides(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Slide Content */}
            <div className="h-96 md:h-[500px] p-8 overflow-y-auto">
              <div className="h-full flex flex-col justify-center">
                {roadmapSlides[currentSlide]?.content}
              </div>
            </div>

            {/* Slide Navigation */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={currentSlide === 0}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Zurück
              </button>

              {/* Slide Dots */}
              <div className="flex space-x-2">
                {roadmapSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index === currentSlide 
                        ? 'bg-blue-600' 
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => setCurrentSlide(Math.min(roadmapSlides.length - 1, currentSlide + 1))}
                disabled={currentSlide === roadmapSlides.length - 1}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Weiter
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;

